"use client";
import { IMAGE_CDN } from "@/constant/apiPath";
import { useAuth } from "@/context/AuthContext";
import useAxiosQuery from "@/hooks/useMyQuery";
import useMyMutation from "@/hooks/userMyMutation";
import { EllipsisOutlined, HeartFilled, HeartOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Input, notification, Select, Skeleton, Typography } from "antd/lib";
import classNames from "classnames";
import dayjs from "dayjs";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTimezoneContext } from "@/context/TimezoneContext";
import { useReportIssueContext } from "@/context/ReportIssueContext";

const { Title } = Typography;

const LIMIT_COMMENT = 5;

enum CommentSortType {
  latest = "latest",
  oldest = "oldest",
  mostLike = "most_liked",
}

const sortOptions = [
  { label: "Latest", value: CommentSortType.latest },
  { label: "Oldest", value: CommentSortType.oldest },
  { label: "Most Liked", value: CommentSortType.mostLike },
];

export const CommentFeed = ({
  postId,
  id,
  isLike,
  totalLike,
  createdAt,
  user,
  content,
  parentId,
  likeCallback,
  isCallMutateComment,
  setIsCallMutateComment,
  isBeginComment,
  setTotalComment,
}: {
  postId: string;
  id?: string;
  isLike?: boolean;
  totalLike?: number;
  totalReply?: number;
  createdAt?: string;
  user?: any;
  content?: string;
  parentId?: string;
  likeCallback: () => any;
  isCallMutateComment?: boolean;
  setIsCallMutateComment?: Dispatch<SetStateAction<boolean>>;
  isBeginComment?: boolean;
  setTotalComment?: Dispatch<SetStateAction<number>>;
}) => {
  const { timezone } = useTimezoneContext();
  const [reply, setReply] = useState(false);
  const [sortType, setSortType] = useState<CommentSortType>(CommentSortType.latest);
  const { openReport } = useReportIssueContext();

  const { data, isLoading, refetch } = useAxiosQuery<any>({
    url: `/v1/blog/comment${id ? `/${id}` : ""}`,
    method: "get",
    params: {
      limit: LIMIT_COMMENT,
      post_id: postId,
      sort: sortType,
    },
  });

  const allComments = useMemo(() => data?.data?.comments || [], [data]);

  useEffect(() => {
    if (isCallMutateComment && setIsCallMutateComment) {
      refetch();
      setIsCallMutateComment(false);
    }
  }, [isCallMutateComment, refetch, setIsCallMutateComment]);

  useEffect(() => {
    if (setTotalComment) {
      setTotalComment(data?.data?.total || 0);
    }
  }, [data?.data?.total, setTotalComment]);

  const { mutate: triggerLikeComment } = useMyMutation({
    url: `/v1/blog/comment/${id}/like`,
    method: "post",
  });

  const handleChangeSort = useCallback(
    (value: CommentSortType) => {
      setSortType(value);
      refetch();
    },
    [refetch],
  );

  return (
    <Skeleton active avatar={true} loading={isLoading}>
      <div className={`flex flex-col ${parentId ? "space-y-2" : "space-y-4"} w-full`}>
        {isBeginComment && (
          <div className="flex items-center justify-between">
            <Title level={5} className="m-0 p-0">
              {data && `${data?.data?.total} ${data?.data?.total > 1 ? "Comments" : "Comment"}`}
            </Title>
            <div className="flex items-center gap-2">
              Sorted by:{" "}
              <Select value={sortType} style={{ width: 120 }} onChange={handleChangeSort} options={sortOptions} />
            </div>
          </div>
        )}
        {content && (
          <div
            className={classNames("flex flex-col gap-2 border border-gray-200 p-2 md:p-5 rounded-lg", {
              "ml-8": parentId,
            })}
          >
            <div className="flex justify-between gap-3">
              <div className="flex gap-3 md:items-center flex-col items-start md:flex-row">
                {user?.profile_information?.avatar ? (
                  <Avatar shape="square" src={`${IMAGE_CDN}${user?.profile_information?.avatar}`} size={32} />
                ) : (
                  <Avatar shape="square" icon={<UserOutlined />} size={32} />
                )}
                <div className="flex flex-col text-[12px] md:text-sm">
                  <span className="font-semibold">{user?.username}</span>
                  <span className="text-gray-600">
                    {dayjs(createdAt).tz(timezone.utc).format("MMMM D, YYYY h:mm A")}
                  </span>
                </div>
              </div>
              <div className="flex flex-nowrap">
                <Button
                  onClick={() => {
                    triggerLikeComment({ post_id: postId }).then(() => likeCallback());
                  }}
                  className="border-none p-0"
                  icon={
                    isLike ? (
                      <HeartFilled width={24} height={24} className="text-gray-500" />
                    ) : (
                      <HeartOutlined width={24} height={24} className="text-gray-500" />
                    )
                  }
                >
                  <Typography.Text type={"secondary"} className={"mx-1"}>
                    {totalLike}
                  </Typography.Text>
                </Button>

                <Dropdown
                  menu={{
                    items: [{ label: "Report", key: "Report", onClick: openReport }],
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<EllipsisOutlined />} type="text" />
                </Dropdown>
              </div>
            </div>

            <Typography.Text>{content ?? ""}</Typography.Text>
            <span className="font-semibold cursor-pointer" onClick={() => setReply(true)}>
              Reply
            </span>

            {reply && (
              <CommentInput
                postId={postId}
                commentCallback={() => {
                  refetch();
                  setReply(false);
                }}
                id={id}
                onCancel={() => setReply(false)}
              />
            )}
          </div>
        )}

        {!!allComments?.length && (
          <div className={classNames("w-full", { "ml-8": parentId })}>
            {allComments?.map((c: any) => (
              <div key={c.id} className={`${parentId ? "mt-2" : "mt-4"}`}>
                <CommentFeed
                  postId={postId}
                  id={c.id}
                  isLike={c.isLiked}
                  totalLike={c.total_like}
                  totalReply={c.total_reply}
                  createdAt={c.created_at}
                  user={c.user_id}
                  content={c.content}
                  parentId={c?.parent}
                  likeCallback={() => refetch()}
                />
              </div>
            ))}
          </div>
        )}
        {!isLoading && data?.data?.comments?.length < data?.data?.total && (
          <Button type={"link"} onClick={() => refetch()}>
            View more comments
          </Button>
        )}
      </div>
    </Skeleton>
  );
};

const CommentInput = ({
  postId,
  id,
  commentCallback,
  onCancel,
}: {
  postId: string;
  id?: string;
  commentCallback: () => any;
  onCancel: () => void;
}) => {
  const { user } = useAuth();

  const [text, setText] = useState("");

  const { mutate: postComment, isPending: isMutating } = useMyMutation({
    url: `/v1/blog/${postId}/comment${id ? `/${id}` : ""}`,
    method: "post",
  });

  const onPost = useCallback(() => {
    if (!text.trim()) {
      return;
    }

    postComment({
      content: text,
    })
      .then(() => {
        setText("");
        commentCallback();
      })
      .catch(e =>
        notification.error({
          message: "Error",
          description: e?.response?.data?.message || "Failed to post comment!",
        }),
      );
  }, [commentCallback, text, postComment]);

  return (
    <div
      className={classNames("flex w-full items-center gap-2", {
        "py-2": id,
        "pb-6 pt-2": !id,
      })}
    >
      {user?.profile_information?.avatar ? (
        <Avatar src={`${IMAGE_CDN}${user?.profile_information?.avatar}`} size={32} />
      ) : (
        <Avatar icon={<UserOutlined />} size={32} />
      )}
      <div className={"flex-grow"}>
        <Input
          onChange={e => setText(e.target.value)}
          value={text}
          onPressEnter={onPost}
          disabled={isMutating}
          className={"w-full"}
          placeholder={"Write your comment"}
        />
      </div>
      <Button type={"primary"} disabled={!text.trim() || isMutating} onClick={onPost}>
        Post
      </Button>
      <Button onClick={onCancel}>Cancel</Button>
    </div>
  );
};
